import { getFirebase } from 'firestorter';

import CollectionName from '../utils/collections';
import BaseDocument from './BaseDocument';

/**
 * ChatMessage class.
 * Each message sent in a chat channel is an instance of this class.
 * @class ChatMessage
 * @extends BaseDocument
 */
class ChatMessage extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CHAT_MESSAGE}/${id}`, opts);
  }

  /**
   * Legacy id of the message (if it was originally sent from getStream).
   */
  get streamMessageId() {
    return this.data.streamMessageId;
  }

  /**
   * Get the text content of the message.
   */
  get text() {
    return this.data.text;
  }

  /**
   * Get the internal text content of the message.
   * Should be visible to coaches only.
   */
  get internalText() {
    return this.data.internalText;
  }

  /**
   * Get the attachments of the message.
   */
  get attachments() {
    return this.data.attachments || [];
  }

  /**
   * Get the sender's id.
   */
  get userId() {
    return this.data.userId;
  }

  /**
   * Get the id of the person that actually sent the message. An AC will send messages on behalf of a coach, so the from
   * property will be the coach's id, and this one will hold the AC's id. If the coach or the user sends the message,
   * this will be the same as the <from> property.
   */
  get initiatedBy() {
    return this.data.initiatedBy;
  }

  /**
   * Get the name of the person that actually sent the message.
   */
  get initiatedByName() {
    return this.data.initiatedByName;
  }

  /**
   * Get the sender's name.
   */
  get userName() {
    return this.data.userName;
  }

  /**
   * Get the id of the real Slack sender snapshotted onto an @Avida-mirrored message
   * (Admin Coaching Channel). Present alongside actualSenderName/actualSenderAvatarUrl.
   */
  get actualSenderId() {
    return this.data.actualSenderId || null;
  }

  /**
   * Get the name of the real Slack sender snapshotted onto an @Avida-mirrored message.
   * Absent on system messages and on historical messages sent before this field existed.
   */
  get actualSenderName() {
    return this.data.actualSenderName || null;
  }

  /**
   * Get the public Slack CDN photo URL of the real sender snapshotted onto an @Avida-mirrored
   * message. May be empty even when actualSenderName is present (no Slack avatar on file).
   */
  get actualSenderAvatarUrl() {
    return this.data.actualSenderAvatarUrl || '';
  }

  /**
   * Get the id of the channel this message was sent on.
   */
  get channelId() {
    return this.data.channelId;
  }

  /**
   * Get the time the message was created. We should use UTC time for all dates, so that we can sort them correctly.
   */
  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  /**
   * Get the time the message was last updated.
   */
  get updatedAt() {
    return this.data.updatedAt?.toDate();
  }

  /**
   * Get whether the message was edited after being sent.
   */
  get isEdited() {
    return this.data.isEdited;
  }

  /**
   * Get whether the message was deleted.
   */
  get isDeleted() {
    return this.data.isDeleted;
  }

  /**
   * Get whether the message triggers push notifications.
   */
  get isSilent() {
    return this.data.isSilent;
  }

  /**
   * Get whether the message is a server-sent system message (not authored by a coach or user).
   */
  get isSystem() {
    return !!this.data.isSystem;
  }

  /**
   * Get the system message display content (emoji title and description text).
   * Only present when isSystem is true.
   */
  get systemMessage() {
    return this.data.systemMessage;
  }

  /**
   * Get the parent details of the message, if any. This will be the id of the message this one is replying to.
   */
  get parent() {
    return this.data.parent;
  }

  /**
   * Get the reactions on the message, keyed by reactor userId. One emoji per user.
   * Shape: { [userId]: emoji }. The @Avida system user uses the AVIDA_REACTION_SENTINEL value.
   */
  get reactions() {
    return this.data.reactions || {};
  }

  /**
   * Get the number of thread replies. Denormalized + incremented server-side.
   */
  get replyCount() {
    return this.data.replyCount ?? 0;
  }

  /**
   * Get whether this message is a thread reply (ADMIN_COACHING channels only).
   */
  get isThreadReply() {
    return !!this.data.isThreadReply;
  }

  /**
   * Get whether the message is flagged for follow-up.
   */
  get isFlagged() {
    return !!this.data.isFlagged;
  }

  /**
   * Get the id of the user assigned to resolve the flagged message.
   */
  get flagAssigneeId() {
    return this.data.flagAssigneeId || null;
  }

  /**
   * Get the time the message was flagged.
   */
  get flagCreatedAt() {
    return this.data.flagCreatedAt?.toDate?.() || null;
  }

  /**
   * Get the id of the user who flagged the message.
   */
  get flagCreatedBy() {
    return this.data.flagCreatedBy || null;
  }

  /**
   * Get the delivery status of the message (e.g. 'failed' when the admin-coaching mirror failed).
   */
  get deliveryStatus() {
    return this.data.deliveryStatus || null;
  }

  /**
   * Get the name of the user assigned to resolve the flagged message.
   */
  get flagAssigneeName() {
    return this.data.flagAssigneeName || null;
  }

  /**
   * Get the number of re-pings sent for this flagged message.
   */
  get flagPingCount() {
    return this.data.flagPingCount || 0;
  }

  /**
   * Get the time the flag was cleared.
   */
  get flagClearedAt() {
    return this.data.flagClearedAt?.toDate?.() || null;
  }

  /**
   * Get the id of the user whose message triggered the flag resolution.
   */
  get flagClearedBy() {
    return this.data.flagClearedBy || null;
  }

  // Methods

  /**
   * delete the message.
   * @param {string} deletedBy The id of the user who deleted the message.
   * @returns {Promise<void>}
   */
  delete = (deletedBy) => (
    this.updateFields({
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy,
    })
  );

  /**
   * Edit the message and add edit time and editor.
   * @param {string} newText New text to be assigned to the message.
   * @param {string} editedBy The id of the user who edited the message.
   * @returns {Promise<void>}
   */
  editText = (newText, editedBy) => (
    this.updateFields({
      text: newText,
      isEdited: true,
      editedAt: new Date(),
      editedBy,
    })
  );

  /**
   * Flag the message for follow-up and assign it to a specific user.
   * @param {string} flaggedBy The id of the user who flagged the message.
   * @param {string} assigneeId The id of the user assigned to resolve the flag.
   * @returns {Promise<void>}
   */
  flag = (flaggedBy, assigneeId, assigneeName, flagReason) => (
    this.updateFields({
      isFlagged: true,
      flagAssigneeId: assigneeId,
      flagAssigneeName: assigneeName,
      flagReason,
      flagCreatedAt: new Date(),
      flagCreatedBy: flaggedBy,
      flagPingCount: 0,
    })
  );

  /**
   * Clear the flag on this message so the 24h Slack re-ping chain stops.
   * Mirrors the fields written server-side by clearFlagsForChannel when a coach replies.
   * The pending ping task is not deleted here - it fires once, sees isFlagged false and stops
   * before notifying Slack.
   * @param {string} clearedBy The id of the user who cleared the flag.
   * @returns {Promise<void>}
   */
  unflag = (clearedBy) => (
    this.updateFields({
      isFlagged: false,
      flagClearedAt: new Date(),
      flagClearedBy: clearedBy,
      flagPingTaskId: getFirebase().firestore.FieldValue.delete(),
    })
  );

  /**
   * Set (or replace) the current user's reaction on this message.
   * Mirrors mobile: reactions.{userId} = emoji. One emoji per user.
   * @param {string} userId The reactor's id.
   * @param {string} emoji The emoji character.
   * @returns {Promise<void>}
   */
  setReaction = (userId, emoji) => (
    this.ref.update({ [`reactions.${userId}`]: emoji })
  );

  /**
   * Remove the current user's reaction from this message.
   * @param {string} userId The reactor's id.
   * @returns {Promise<void>}
   */
  removeReaction = (userId) => (
    this.ref.update({
      [`reactions.${userId}`]: getFirebase().firestore.FieldValue.delete(),
    })
  );

  static async getById(id) {
    const chatMessageDoc = new ChatMessage(id);
    await chatMessageDoc.init();
    return chatMessageDoc;
  }
}

export default ChatMessage;
